import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Grid, Typography } from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { RootStoreContext } from '../../global/storeContext';
import { StripeAccountComponent } from './psp/stripe';
import { useIdentityGate } from '../../hooks/useIdentityGate';
import { CompleteAccountSetupDialog } from '../rentApplication/dialogs/completeAccountSetupDialog';

export const BankAccountComponent: React.FC = observer(() => {
  const { bankAccountStore, uiStore, userStore } = useContext(RootStoreContext);
  const [connectedAccountId, setConnectedAccountId] = useState<string>();
  const { checkAndProceed, gateDialogProps } = useIdentityGate();

  useEffect(() => {
    const init = async () => {
      if (!userStore.userId) return;
      uiStore.showLoading();
      try {
        // Check if user already has a connected account and assign connectedAccountId using setConnectedAccountId. Don't auto-create account if none exists, wait for user to click "Connect Account"
        const accountId = await bankAccountStore.getStripeConnectAccountId();
        if (accountId) {
          setConnectedAccountId(accountId);
        }
      } catch (err) {
        console.log('No existing connected account found');
      } finally {
        uiStore.hideLoading();
      }
    };

    init();
  }, []);

  const startOnboarding = () =>
    checkAndProceed(async () => {
      uiStore.showLoading();
      try {
        const accountId = await bankAccountStore.createStripeConnectedAccount();
        if (!accountId) {
          console.error('Failed to create Stripe Connected Account.');
          uiStore.error(
            'We are unable to connect your bank account at this time. Please try again later.',
          );
          return;
        }
        setConnectedAccountId(accountId);
      } finally {
        uiStore.hideLoading();
      }
    });

  return (
    <>
      <Grid
        container
        style={{ marginTop: '24px', marginBottom: '40px' }}
        justifyContent='space-between'
        spacing={2}
      >
        <Grid item>
          <div>
            <Typography variant='h6' gutterBottom>
              Connected Account
            </Typography>
            <Typography gutterBottom variant='body1' color='textSecondary'>
              Connect a bank account. This will be used to receive rent/ bill payments from your
              tenants.
            </Typography>
          </div>
        </Grid>
        <Grid item>
          <Typography display='inline'>
            <Button
              variant='outlined'
              color='primary'
              size='small'
              onClick={() => startOnboarding()}
              disabled={!!connectedAccountId || uiStore.loading}
            >
              Connect Account
            </Button>
          </Typography>
        </Grid>
      </Grid>

      {connectedAccountId && <StripeAccountComponent accountId={connectedAccountId} />}

      <CompleteAccountSetupDialog {...gateDialogProps} />
    </>
  );
});
